import type { EChartsTheme } from '../types';
import { horizontalCornerRadiusFor } from '../shared/barRadius';
import type { SidewaysBarDatum, SidewaysBarChartConfig } from './types';

// Matches amcharts4: columns.template.height = 85%, maxHeight = 30.
const BAR_MAX_WIDTH = 30;

/**
 * Builds the single horizontal bar series. Each bar carries its own color,
 * sign-based corner radius, and value label positioned at its outer tip.
 */
export const buildSidewaysBarSeries = (
  data: SidewaysBarDatum[],
  config: SidewaysBarChartConfig,
  theme: EChartsTheme,
) => {
  const { privacyMask, clickable = false, animated = true } = config;

  const seriesData = data.map((datum) => {
    const isPositive = datum.value >= 0;
    return {
      // Preserve row identity across sorting and drill-down updates. Display categories can repeat
      // in report data, so using the label alone breaks ECharts' diff/animation matching.
      id: datum.id ?? datum.category,
      name: datum.category,
      value: datum.value,
      itemStyle: {
        color: datum.color,
        borderRadius: horizontalCornerRadiusFor(datum.value),
      },
      label: {
        show: true,
        // Amount sits just past the bar's outer tip.
        position: isPositive ? ('right' as const) : ('left' as const),
        distance: 8,
        color: theme.textSecondaryColor,
        fontSize: 14,
        formatter: () => String(privacyMask(datum.label)),
      },
    };
  });

  return [
    {
      type: 'bar' as const,
      name: 'groups',
      data: seriesData,
      barWidth: '85%',
      barMaxWidth: BAR_MAX_WIDTH,
      // Drill-down commonly replaces every category ID, so declare the series animation directly
      // instead of relying on the global option's update defaults.
      animationDuration: animated ? 600 : 0,
      animationDurationUpdate: animated ? 300 : 0,
      animationEasingUpdate: 'cubicOut' as const,
      // Selection/hover dimming isn't part of the amcharts original — keep it flat.
      emphasis: { disabled: true as const },
      cursor: clickable ? 'pointer' : 'default',
    },
  ];
};
